import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Switch,
  Space,
  Typography,
  Popconfirm,
  message,
  Tooltip,
  Statistic,
  Row,
  Col,
  Empty,
} from 'antd';
import {
  TagsOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CrownOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { subscriptionPlansService } from '../services/subscriptionPlansService';

const { Title, Text } = Typography;

const couponStatus = (c) => {
  if (c.isActive === false) return { label: 'Inactive', color: 'default' };
  if (c.validUntil && dayjs(c.validUntil).isBefore(dayjs(), 'day')) return { label: 'Expired', color: 'red' };
  if (c.maxRedemptions > 0 && (c.redeemedCount || 0) >= c.maxRedemptions) {
    return { label: 'Exhausted', color: 'orange' };
  }
  return { label: 'Active', color: 'green' };
};

const Coupons = () => {
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const refresh = async () => {
    setLoading(true);
    try {
      const [couponsRes, plansRes] = await Promise.all([
        subscriptionPlansService.listAllCoupons(),
        subscriptionPlansService.getPlans({ limit: 100 }),
      ]);
      setCoupons(couponsRes?.data?.items || []);
      setPlans(plansRes?.data?.plans || []);
    } catch (err) {
      message.error(err.message || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const stats = useMemo(() => {
    const active = coupons.filter((c) => couponStatus(c).label === 'Active').length;
    const expired = coupons.filter((c) => couponStatus(c).label === 'Expired').length;
    const totalRedemptions = coupons.reduce((s, c) => s + (c.redeemedCount || 0), 0);
    return { total: coupons.length, active, expired, totalRedemptions };
  }, [coupons]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      maxRedemptions: 0,
      discountPercent: 10,
    });
    setDrawerOpen(true);
  };

  const openEdit = (coupon) => {
    setEditing(coupon);
    form.setFieldsValue({
      planId: coupon.planId,
      code: coupon.code,
      discountPercent: coupon.discountPercent,
      maxRedemptions: coupon.maxRedemptions || 0,
      validUntil: coupon.validUntil ? dayjs(coupon.validUntil) : null,
      isActive: coupon.isActive !== false,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      code: String(values.code).trim().toUpperCase(),
      discountPercent: Number(values.discountPercent),
      maxRedemptions: Number(values.maxRedemptions) || 0,
      validUntil: values.validUntil ? values.validUntil.toISOString() : null,
      isActive: !!values.isActive,
    };
    setSaving(true);
    try {
      if (editing) {
        await subscriptionPlansService.updateCoupon(editing.planId, editing._id, payload);
        message.success(`Coupon ${payload.code} updated`);
      } else {
        await subscriptionPlansService.createCoupon(values.planId, payload);
        message.success(`Coupon ${payload.code} created`);
      }
      setDrawerOpen(false);
      setEditing(null);
      refresh();
    } catch (err) {
      message.error(err.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    try {
      await subscriptionPlansService.deleteCoupon(coupon.planId, coupon._id);
      message.success(`Coupon ${coupon.code} deleted`);
      refresh();
    } catch (err) {
      message.error(err.message || 'Failed to delete coupon');
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      render: (c) => (
        <Tag color="purple" style={{ fontFamily: 'monospace', fontSize: 13, padding: '2px 8px' }}>
          <GiftOutlined /> {c}
        </Tag>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discountPercent',
      align: 'right',
      render: (v) => <Text strong style={{ color: '#10b981' }}>{v}% off</Text>,
    },
    {
      title: 'Plan',
      dataIndex: 'planName',
      render: (name, row) => (
        <Space size={4}>
          <CrownOutlined style={{ color: '#8b5cf6' }} />
          <Text>{name}</Text>
          <Tag style={{ marginLeft: 4, textTransform: 'capitalize' }}>{row.planType}</Tag>
        </Space>
      ),
    },
    {
      title: 'Usage',
      align: 'right',
      render: (_, row) => {
        const max = row.maxRedemptions || 0;
        const used = row.redeemedCount || 0;
        return (
          <Tooltip title={max === 0 ? 'No redemption limit' : `${max - used} remaining`}>
            <Text>{used}{max > 0 ? ` / ${max}` : ' / ∞'}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Valid until',
      dataIndex: 'validUntil',
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : <Text type="secondary">No expiry</Text>),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, row) => {
        const s = couponStatus(row);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 140,
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Popconfirm
            title="Delete this coupon?"
            description={`Code "${row.code}" will be permanently removed.`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <TagsOutlined style={{ marginRight: 8, color: '#8b5cf6' }} />
            Coupons &amp; Discounts
          </Title>
          <Text type="secondary">Create promo codes that tenants can apply at checkout. Each coupon is tagged to one plan.</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New coupon
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total coupons" value={stats.total} prefix={<TagsOutlined />} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Active" value={stats.active} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#10b981' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Expired" value={stats.expired} prefix={<StopOutlined />} valueStyle={{ color: '#ef4444' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total redemptions" value={stats.totalRedemptions} prefix={<GiftOutlined />} valueStyle={{ color: '#8b5cf6' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          rowKey={(r) => `${r.planId}-${r._id}`}
          dataSource={coupons}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No coupons yet. Click <strong>New coupon</strong> to create one and tag it to a plan.
                  </span>
                }
              />
            ),
          }}
        />
      </Card>

      <Drawer
        title={editing ? `Edit coupon: ${editing.code}` : 'New coupon'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={480}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeDrawer} disabled={saving}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit} loading={saving}>
              {editing ? 'Save changes' : 'Create coupon'}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item
            name="planId"
            label="Subscription plan"
            rules={[{ required: true, message: 'Pick a plan to tag this coupon to' }]}
            extra="Coupons are scoped to one plan. To offer the same code on multiple plans, create one coupon per plan."
          >
            <Select
              disabled={!!editing}
              placeholder="Select a plan"
              showSearch
              optionFilterProp="label"
              options={plans.map((p) => ({
                value: p._id,
                label: `${p.name} (${p.planType})`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="code"
            label="Coupon code"
            rules={[
              { required: true, message: 'Code is required' },
              { pattern: /^[A-Z0-9_-]+$/i, message: 'Letters, digits, dash and underscore only' },
              { min: 3, max: 32, message: '3–32 characters' },
            ]}
            extra="Stored uppercase. Example: LAUNCH50, FY26-PRO."
          >
            <Input placeholder="e.g. LAUNCH50" style={{ textTransform: 'uppercase', fontFamily: 'monospace' }} />
          </Form.Item>

          <Form.Item
            name="discountPercent"
            label="Discount (%)"
            rules={[{ required: true, message: 'Discount % is required' }]}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} addonAfter="%" />
          </Form.Item>

          <Form.Item
            name="maxRedemptions"
            label="Max redemptions"
            extra="0 means unlimited."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="validUntil"
            label="Valid until"
            extra="Leave blank for no expiry."
          >
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d && d.isBefore(dayjs(), 'day')} />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Paused" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default Coupons;
